import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';

import { environment } from '@environments/environment';
import { ErrorComponent } from '@errors/error.component';
import { AuthService } from '@providers/auth/auth.service';
import { GlobalDataService } from '@providers/global-data/global-data.service';
import { ModelsToolsService } from '@providers/models-tools/models-tools.service';
import { RequestService } from '@providers/request/request.service';
import { RequestV2Service } from '@providers/request-service-v2/request-v2.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less']
})
export class AppComponent implements OnInit {
  user: any;
  token: string;
  title = 'customer-area';
  isStandAlone: boolean;
  isInit: boolean;
  isLoadingProject: boolean;
  currentProject: any;
  state: string = 'idle';
  errorMessage: string;
  errorLink: string;
  isFirstConnection: boolean = false;
  isMenuOpen: boolean = false;
  closePopups: boolean = false;

  @ViewChild('mainContent', { static: false }) mainContent;
  @ViewChild('mainContainer', { static: false }) mainContainer;

  constructor(
    private router: Router,
    private authService: AuthService,
    private globalDataService: GlobalDataService,
    private requestService: RequestService,
    private requestV2Service: RequestV2Service,
    private modelsToolsService: ModelsToolsService
  ) {
    if (localStorage.hasOwnProperty('access_token')) {
      localStorage.removeItem('access_token');
    }

    this.disableErrorProd();
    this.authService.retrieveTokenFromUrl();

    this.isStandAlone = false;
    this.isInit = false;
    this.isLoadingProject = false;
    this.currentProject = null;

    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.startsWith('/quotations/')) {
          this.isStandAlone = true;
        }

        this.updateBodyPageClass(event.url);

        this.closeMenu();

        const projectId = this.retrieveProjectId(event.url);
        const isNewProject = !this.currentProject || this.currentProject.id !== projectId;

        if (this.isInit && projectId && isNewProject) {
          this.requestAndNavigateToProject(projectId);
        }
      }
    });
  }

  async ngOnInit() {
    if (this.authService.isAuthenticated()) {
      this.retrieveUserInformationsData();
    } else {
      this.authService.redirectOAuth();
    }

    const projectStepCategoryResult = await this.requestV2Service.getStepCategories();
    this.globalDataService.stepsCategory = projectStepCategoryResult.items;
    const stepResult = await this.requestV2Service.getProjectSteps();
    this.globalDataService.steps = stepResult.items;
    this.globalDataService.steps = this.globalDataService.steps
      .sort((a: any, b: any) => a.order - b.order);

    const units = await this.requestV2Service.getUnitTypes();
    this.globalDataService.units = units.items;
  }

  componentAdded(event) {
    if (event instanceof ErrorComponent) {
      return;
    }

    if (event.openMenu) {
      event.openMenu.subscribe(() => {
        this.openMenu();
      });
    }
  }

  updateBodyPageClass(url: string) {
    let currentPage = url.substring(url.lastIndexOf('/') + 1);

    const queryStringIndex = currentPage.indexOf('?');
    if (queryStringIndex !== -1) {
      currentPage = currentPage.substring(0, queryStringIndex);
    }

    // Remove previous page-* classes
    const currentClasses = this.mainContainer.nativeElement.classList;

    currentClasses.forEach((className) => {
      if (className.indexOf('page-') === 0) {
        this.removeClass(this.mainContainer, className);
      }
    });

    this.addClass(this.mainContainer, `page-${currentPage}`);
  }

  addClass(element: ElementRef, className: string) {
    if (!element) {
      return;
    }

    if (!element.nativeElement.classList.contains(className)) {
      element.nativeElement.classList.add(className);
    }
  }

  removeClass(element: ElementRef, className: string) {
    if (!element) {
      return;
    }

    element.nativeElement.classList.remove(className);
  }

  openMenu() {
    this.addClass(this.mainContent, 'menu-opened');
  }

  closeMenu() {
    this.removeClass(this.mainContent, 'menu-opened');
  }

  private initProjectsList(data): any[] {
    const projects: any[] = [];

    data.forEach((project) => {
      const lightProject: any = { ...project };

      projects.push(lightProject);
    });

    return projects;
  }

  public async retrieveUserInformationsData(): Promise<any> {
    this.state = 'loading';

    try {
      const accountInformation = await this.requestV2Service.getAccount();
      this.user = accountInformation;
      this.globalDataService.user = accountInformation;

      if (this.isStandAlone) {
        this.state = 'ready';
        return;
      }

      const projectsResult = await this.requestV2Service.getProjects(this.user.id, true, true);
      const projects = this.initProjectsList(projectsResult.items);

      if (projects.length <= 0) {
        this.onError(new Error('Aucun projet disponible.'));
        return;
      }

      this.globalDataService.setProjects(projects);
      this.isInit = true;

      let projectId = this.retrieveProjectId(this.router.url);
      if (!projectId) {
        projectId = this.retrieveDefaultProjectId(projects);
        this.router.navigate([`/projects/${projectId}/dashboard`]);
        return;
      }
      this.requestAndNavigateToProject(projectId);

    } catch (e) {
      this.onError(new Error('Impossible de récupérer la liste des projets.'));
    }
  }

  private async requestAndNavigateToProject(projectId: string): Promise<any> {
    if (this.isLoadingProject) {
      return;
    }

    // Check project existence
    const projectsResult: any = this.globalDataService.getProjects();

    if (projectsResult && projectsResult.items && !this.isProjectExisting(projectsResult.items, projectId)) {
      this.onError(new Error("Vous n'avez pas accès à ce projet."));

      return;
    }

    this.isLoadingProject = true;
    this.state = this.router.url.includes('materials') ? this.state : 'loading';

    try {
      this.currentProject = await this.requestV2Service.getProject(projectId, true);
      this.checkWrongRoutes();

      this.globalDataService.setCurrentProjectId(this.currentProject.id);
      this.globalDataService.setCurrentProject(this.currentProject);

      if (!this.modelsToolsService.isStepValid(this.currentProject._related.steps[0].id)) {
        this.onError(new Error('Ce projet est encore en cours de finalisation.'));
        this.errorLink = `https://monprojet.littleworker.fr/projects/${
          this.currentProject.id
        }/`;
        return;
      }

      if (localStorage.getItem('isFirstConnection') !== 'false') {
        this.isFirstConnection = true;
        localStorage.setItem('isFirstConnection', 'false');
      }

      this.isLoadingProject = false;
      this.state = 'ready';
    } catch (err) {
      this.currentProject = null;
      this.globalDataService.setCurrentProject(null);

      this.isLoadingProject = false;

      switch (err.message) {
        // TODO: Handle other case
        default:
          this.onError(new Error("Le projet que vous recherchez n'est pas ou plus disponible."));
          break;
      }
    }
  }

  private onError(error: Error) {
    this.errorMessage = error.message;
    this.state = 'error';
  }

  private retrieveProjectId(url: string): string {
    const args = url.split('/');
    let projectId: string = null;

    if (args.length >= 3) {
      projectId = args[2];
    }

    return projectId;
  }

  private retrieveDefaultProjectId(projects: any[]): string {
    if (projects === null || projects.length <= 0) {
      return null;
    }

    return projects[0].id;
  }

  private isProjectExisting(projects: any[], projectId: string): boolean {
    if (!projects || projects.length <= 0) {
      return false;
    }

    const found = projects.find(i => i.id === projectId);

    return !!found;
  }

  private disableErrorProd(): void {
    if (environment.production) {
      window.console.log = function () { };
    }
  }

  private checkWrongRoutes(): void {
    const categoryRange = this.globalDataService.stepsCategory
      .filter(i => i.name !== 'Études' || i.name !== 'S.A.V' || i.name !== 'Travaux')
      .map(i => i.id);

    const stepsRange = this.globalDataService.steps
      .filter(i => categoryRange.includes(i.id_project_step_category));

    const currentRoute = window.location.pathname.split('/');

    if (typeof stepsRange.find(i => currentRoute.includes(i.id)) !== 'undefined') {
      this.router.navigate(['projects', this.currentProject.id, 'dashboard']);
    }
  }

  public displayMask(event: boolean) {
    this.isMenuOpen = event;
    this.closePopups = false;

  }

  public inClosePopups() {
    this.closePopups = true;
    this.isMenuOpen = false;
  }
}
